import { Badge, Stack, Text } from '@mantine/core'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
	downloadChapterText,
	editPublishedChapter,
	getChapterDetails,
	getChapterProcessingStatus,
	parseChapterDetails,
	parseChapterStatus,
	publishChapters,
	unpublishChapter,
} from 'src/apis/bookAPI'
import ChapterPublishFooter from 'src/components/BookUniVini/ChapterPublishFooter'
import LanguageFileSection from 'src/components/BookUniVini/LanguageFileSection'
import CustomButton from 'src/components/CustomComponents/CustomButton'
import CustomDrawer from 'src/components/CustomComponents/CustomDrawer'
import { useModal } from 'src/context/ModalContext'
import {
	mergeChapterStatusUpdate,
	useChapterStatusPoll,
} from 'src/hooks/useChapterStatusPoll'
import { BookData, ChapterAudioDialectVariant, ChapterData, ChapterEditLanguageSection } from 'src/interfaces'
import { buildChapterEditSections } from 'src/utils/chapterEditView'
import {
	buildRegenerateAudioPayload,
	buildRegenerateTextPayload,
	findAudioDetailForRegenerate,
} from 'src/utils/chapterAudioRegenerate'
import {
	applyRemovedSlotsToSections,
	audioSlotKey,
	docSlotKey,
} from 'src/utils/chapterLocalRemove'
import { collectHighlightRegenTargets } from 'src/utils/chapterHighlightRegen'
import { downloadFileUrl } from 'src/utils/fileUrl'

interface ChapterEditDrawerProps {
	opened: boolean
	onClose: () => void
	book: BookData | null
	chapter: ChapterData | null
	onRefresh?: () => void | Promise<void>
}

const getPublishedStatusColor = (status?: string) => {
	const normalized = String(status || '').toLowerCase()
	if (normalized === 'published') return 'green'
	if (normalized === 'unpublished') return 'gray'
	return 'yellow'
}

const mergeChapterData = (
	listChapter: ChapterData,
	statusChapter: ChapterData | null,
	detailsChapter: ChapterData | null,
): ChapterData => {
	const merged = {
		...listChapter,
		...(detailsChapter ?? {}),
		...(statusChapter ?? {}),
		id: listChapter.id ?? statusChapter?.id ?? detailsChapter?.id,
		original_doc_url:
			statusChapter?.original_doc_url ??
			detailsChapter?.original_doc_url ??
			listChapter.original_doc_url ??
			null,
		content:
			detailsChapter?.content?.length
				? detailsChapter.content
				: listChapter.content ?? detailsChapter?.content,
		audio_details:
			statusChapter?.audio_details?.length
				? statusChapter.audio_details
				: detailsChapter?.audio_details ?? listChapter.audio_details ?? [],
	}

	return merged
}

const extractDownloadUrl = (response: any): string | null => {
	const payload = response?.data?.results?.object
		?? response?.data?.results
		?? response?.data

	if (typeof payload === 'string') return payload
	if (typeof payload?.url === 'string') return payload.url
	if (typeof payload?.doc_url === 'string') return payload.doc_url
	if (typeof payload?.file_url === 'string') return payload.file_url
	return null
}

const isSuccessfulResponse = (res: any) =>
	res?.data?.code === 200 || res?.status === 200 || res?.data?.code === 201

function ChapterEditDrawer({
	opened,
	onClose,
	book,
	chapter,
	onRefresh,
}: ChapterEditDrawerProps) {
	const { openError, openModal } = useModal()
	const [loading, setLoading] = useState(false)
	const [actionLoading, setActionLoading] = useState(false)
	const [chapterData, setChapterData] = useState<ChapterData | null>(chapter)
	const [regeneratingAudioId, setRegeneratingAudioId] = useState<string | null>(null)
	const [regeneratingDocId, setRegeneratingDocId] = useState<string | null>(null)
	const [removedSlots, setRemovedSlots] = useState<Set<string>>(() => new Set())

	const loadChapterData = useCallback(async () => {
		if (!book?.id || !chapter?.id) return

		setLoading(true)
		try {
			const [statusRes, detailsRes] = await Promise.allSettled([
				getChapterProcessingStatus(chapter.id),
				getChapterDetails(chapter.id),
			])

			const statusChapter =
				statusRes.status === 'fulfilled'
					? parseChapterStatus(statusRes.value)
					: null
			const detailsChapter =
				detailsRes.status === 'fulfilled'
					? parseChapterDetails(detailsRes.value)
					: null

			setChapterData(mergeChapterData(chapter, statusChapter, detailsChapter))
		} catch (error) {
			setChapterData(chapter)
			openError(error)
		} finally {
			setLoading(false)
		}
	}, [book?.id, chapter, openError])

	useEffect(() => {
		if (!opened) return
		setChapterData(chapter)
		setRemovedSlots(new Set())
		loadChapterData()
	}, [opened, chapter, loadChapterData])

	const handleStatusPoll = useCallback((statusChapter: ChapterData | null) => {
		if (!statusChapter) return
		setChapterData((current) => mergeChapterStatusUpdate(current, statusChapter))
	}, [])

	useChapterStatusPoll(chapter?.id, opened, handleStatusPoll)

	const sections = useMemo(() => {
		if (!chapterData) {
			return { languageSections: [] }
		}

		const built = buildChapterEditSections(chapterData)
		return {
			languageSections: applyRemovedSlotsToSections(
				built.languageSections,
				removedSlots,
			),
		}
	}, [chapterData, removedSlots])

	const highlightRegenTargets = useMemo(
		() => collectHighlightRegenTargets(sections.languageSections),
		[sections.languageSections],
	)

	const runEditPublishedAction = useCallback(
		async (
			payload: Parameters<typeof editPublishedChapter>[1],
			successLabel: string,
			options?: { notifySuccess?: boolean; reload?: boolean; manageLoading?: boolean },
		) => {
			if (!chapterData?.id) return false

			const {
				notifySuccess = true,
				reload = true,
				manageLoading = true,
			} = options ?? {}

			if (manageLoading) setActionLoading(true)
			setChapterData((current) =>
				current ? { ...current, ready_to_publish: false } : current,
			)

			try {
				const res = await editPublishedChapter(chapterData.id, payload)
				if (isSuccessfulResponse(res)) {
					if (notifySuccess) {
						openModal({ type: 'success', label: successLabel })
					}
					if (reload) {
						await loadChapterData()
						await onRefresh?.()
					}
					return true
				}
				return false
			} catch (error) {
				openError(error)
				return false
			} finally {
				if (manageLoading) setActionLoading(false)
			}
		},
		[chapterData?.id, loadChapterData, onRefresh, openError, openModal],
	)

	const handleDownloadDoc = useCallback(
		async (language: string) => {
			if (!chapterData?.id) return

			setActionLoading(true)
			try {
				const res = await downloadChapterText(chapterData.id, language)
				const downloadUrl = extractDownloadUrl(res)

				if (downloadUrl) {
					downloadFileUrl(downloadUrl)
					return
				}

				const section = sections.languageSections.find(
					(item) => item.language === language,
				)
				if (section?.docUrl) {
					downloadFileUrl(section.docUrl)
					return
				}

				openError(new Error('Download URL not found'))
			} catch (error) {
				openError(error)
			} finally {
				setActionLoading(false)
			}
		},
		[chapterData?.id, openError, sections.languageSections],
	)

	const handleRegenerateAudio = useCallback(
		(variant: ChapterAudioDialectVariant) => {
			if (!chapterData?.id || !variant.bookAudioId) {
				openError(new Error('Missing book_audio_id for regenerate'))
				return
			}

			const matchedDetail = findAudioDetailForRegenerate(
				chapterData.audio_details,
				variant.bookAudioId,
				variant.language_variant,
				variant.language,
			)

			if (!matchedDetail) {
				openError(new Error('Audio slot not found for selected variant'))
				return
			}

			openModal({
				type: 'confirm',
				label: `Regenerate audio for ${variant.label} only?`,
				handleConfirm: async () => {
					setRegeneratingAudioId(matchedDetail.id)
					await runEditPublishedAction(
						buildRegenerateAudioPayload(matchedDetail),
						`Regenerating ${variant.label}`,
					)
					setRegeneratingAudioId(null)
				},
			})
		},
		[chapterData, openError, openModal, runEditPublishedAction],
	)

	const handleRegenerateHighlightAudios = useCallback(() => {
		if (!highlightRegenTargets.length) return

		const targetLabels = highlightRegenTargets.map((variant) => variant.label).join(', ')

		openModal({
			type: 'confirm',
			label: `Regenerate audio for highlight issues?\n\n${targetLabels}`,
			handleConfirm: async () => {
				setActionLoading(true)
				let successCount = 0

				try {
					for (const variant of highlightRegenTargets) {
						const matchedDetail = findAudioDetailForRegenerate(
							chapterData?.audio_details,
							variant.bookAudioId,
							variant.language_variant,
							variant.language,
						)

						if (!matchedDetail) continue

						setRegeneratingAudioId(matchedDetail.id)
						const ok = await runEditPublishedAction(
							buildRegenerateAudioPayload(matchedDetail),
							`Regenerating ${variant.label}`,
							{ notifySuccess: false, reload: false, manageLoading: false },
						)
						if (ok) successCount += 1
					}

					if (successCount > 0) {
						await loadChapterData()
						await onRefresh?.()
						openModal({
							type: 'success',
							label: `Regenerating ${successCount} audio(s) for highlight issues`,
						})
					}
				} finally {
					setRegeneratingAudioId(null)
					setActionLoading(false)
				}
			},
		})
	}, [
		chapterData?.audio_details,
		highlightRegenTargets,
		loadChapterData,
		onRefresh,
		openModal,
		runEditPublishedAction,
	])

	const resolveDocDetail = useCallback(
		(section: ChapterEditLanguageSection) => {
			if (!section.docBookAudioId) return null

			return findAudioDetailForRegenerate(
				chapterData?.audio_details,
				section.docBookAudioId,
				null,
				section.language,
			)
		},
		[chapterData?.audio_details],
	)

	const handleRegenerateDoc = useCallback(
		(section: ChapterEditLanguageSection) => {
			const matchedDetail = resolveDocDetail(section)
			if (!matchedDetail) {
				openError(new Error('Document slot not found for selected language'))
				return
			}

			openModal({
				type: 'confirm',
				label: `Regenerate document for ${section.label} only?`,
				handleConfirm: async () => {
					setRegeneratingDocId(matchedDetail.id)
					await runEditPublishedAction(
						buildRegenerateTextPayload(matchedDetail),
						`Regenerating ${section.label} document`,
					)
					setRegeneratingDocId(null)
				},
			})
		},
		[openError, openModal, resolveDocDetail, runEditPublishedAction],
	)

	const handleRemoveDoc = useCallback((section: ChapterEditLanguageSection) => {
		setRemovedSlots((current) => {
			const next = new Set(current)
			next.add(docSlotKey(section.language))
			return next
		})
	}, [])

	const handleRemoveAudio = useCallback((variant: ChapterAudioDialectVariant) => {
		setRemovedSlots((current) => {
			const next = new Set(current)
			next.add(audioSlotKey(variant.bookAudioId))
			return next
		})
	}, [])

	const handlePublish = useCallback(() => {
		if (!chapterData?.id) return

		openModal({
			type: 'confirm',
			label: 'Publish this chapter now?',
			handleConfirm: async () => {
				setActionLoading(true)
				try {
					const res = await publishChapters([chapterData.id])
					if (isSuccessfulResponse(res)) {
						openModal({ type: 'success', label: 'Chapter published' })
						await loadChapterData()
						await onRefresh?.()
					}
				} catch (error) {
					openError(error)
				} finally {
					setActionLoading(false)
				}
			},
		})
	}, [chapterData?.id, loadChapterData, onRefresh, openError, openModal])

	const handleUnpublish = useCallback(() => {
		if (!chapterData?.id) return

		openModal({
			type: 'confirm',
			label: 'Unpublish this chapter now?',
			handleConfirm: async () => {
				setActionLoading(true)
				try {
					const res = await unpublishChapter(chapterData.id)
					if (isSuccessfulResponse(res)) {
						openModal({ type: 'success', label: 'Chapter unpublished' })
						await loadChapterData()
						await onRefresh?.()
					}
				} catch (error) {
					openError(error)
				} finally {
					setActionLoading(false)
				}
			},
		})
	}, [chapterData?.id, loadChapterData, onRefresh, openError, openModal])

	const publishedStatus = chapterData?.published_status || 'unknown'
	const isPublished = String(publishedStatus).toLowerCase() === 'published'
	const readyToPublish = chapterData?.ready_to_publish === true
	const chapterLabel = chapterData?.chapter_number
		? `Ch.${chapterData.chapter_number}`
		: 'Chapter'

	const progressLabel =
		typeof chapterData?.percentage_complete === 'number'
			? `${chapterData.percentage_complete}% complete`
			: null

	return (
		<CustomDrawer
			opened={opened}
			onClose={onClose}
			width={640}
			loading={loading || actionLoading}
			title={
				<Stack spacing={4}>
					<Text size="xs" color="dimmed">
						UniVini · {isPublished ? 'Published' : 'Unpublished'}
					</Text>
					<Text weight={700}>{chapterData?.title || 'Chapter'}</Text>
					<Stack spacing={6}>
						<Text size="sm" color="dimmed">
							{book?.title || '-'} · {chapterLabel}
						</Text>
						<Stack spacing={4}>
							<Badge
								variant="light"
								color={getPublishedStatusColor(publishedStatus)}
								sx={{ width: 'fit-content', textTransform: 'capitalize' }}
							>
								{publishedStatus}
							</Badge>
							{readyToPublish ? (
								<Badge
									variant="light"
									color="teal"
									sx={{ width: 'fit-content' }}
								>
									Ready to publish
								</Badge>
							) : (
								<Badge
									variant="light"
									color="orange"
									sx={{ width: 'fit-content' }}
								>
									Not ready to publish
								</Badge>
							)}
							{progressLabel && (
								<Text size="xs" color="dimmed">
									{progressLabel}
									{typeof chapterData?.completed_audios === 'number' &&
									typeof chapterData?.total_audios === 'number'
										? ` · ${chapterData.completed_audios}/${chapterData.total_audios} audios`
										: ''}
								</Text>
							)}
						</Stack>
					</Stack>
				</Stack>
			}
			footer={
				<ChapterPublishFooter
					publishedStatus={publishedStatus}
					readyToPublish={readyToPublish}
					loading={loading || actionLoading}
					onPublish={handlePublish}
					onUnpublish={handleUnpublish}
				/>
			}
		>
			<Stack spacing={20} sx={{ padding: 16 }}>
				{highlightRegenTargets.length > 0 && (
					<Stack spacing={6}>
						<CustomButton
							fullWidth
							onClick={handleRegenerateHighlightAudios}
							disabled={loading || actionLoading}
							sx={{
								height: 44,
								border: 'none',
								color: '#fff',
								background: loading || actionLoading ? '#d1d5db' : '#dc2626',
								borderRadius: 999,
								':hover': {
									background:
										loading || actionLoading
											? '#d1d5db !important'
											: '#b91c1c !important',
									color: '#fff',
								},
							}}
						>
							Re-gen highlight audio ({highlightRegenTargets.length})
						</CustomButton>
						<Text size="xs" color="dimmed">
							Missing or incorrect highlight only. Excludes English (UK), Vietnamese
							(North), Vietnamese (South).
						</Text>
					</Stack>
				)}

				<Text size="sm" color="dimmed">
					Manage published audio and document files for this chapter.
				</Text>

				<Stack spacing={16}>
					<Text
						size="xs"
						weight={700}
						sx={{ textTransform: 'uppercase', color: '#6b7280', letterSpacing: 0.4 }}
					>
						All files
					</Text>

					{sections.languageSections.length ? (
						sections.languageSections.map((section) => (
							<LanguageFileSection
								key={section.language}
								section={section}
								onDownloadDoc={handleDownloadDoc}
								onRegenerateDoc={handleRegenerateDoc}
								onRemoveDoc={handleRemoveDoc}
								onRegenerateAudio={handleRegenerateAudio}
								onRemoveAudio={handleRemoveAudio}
								regeneratingAudioId={regeneratingAudioId}
								regeneratingDocId={regeneratingDocId}
							/>
						))
					) : (
						<Text size="sm" color="dimmed">
							No language files found — audio slots may not be initialized yet
						</Text>
					)}
				</Stack>
			</Stack>
		</CustomDrawer>
	)
}

export default ChapterEditDrawer
